import React, { useState, useEffect, useCallback } from 'react';
import { brainDigestAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Mail, Send, Clock, Eye, Plus, X, History, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeHtml } from '../../lib/sanitize';

export default function BrainDigest() {
  const [config, setConfig] = useState({ enabled: false, frequency: 'weekly', recipients: [], day_of_week: 1, hour_utc: 8 });
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [configRes, histRes] = await Promise.all([
        brainDigestAPI.getConfig(),
        brainDigestAPI.getHistory(),
      ]);
      setConfig(configRes.data);
      setHistory(histRes.data?.history || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Include any email typed but not yet "+"-added so it is never silently dropped.
      const pending = newEmail.trim().toLowerCase();
      let recipients = config.recipients || [];
      if (pending && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !recipients.includes(pending)) {
        recipients = [...recipients, pending];
      }
      const payload = { ...config, recipients };
      await brainDigestAPI.saveConfig(payload);
      setConfig(payload);
      setNewEmail('');
      toast.success(
        recipients.length
          ? `Digest settings saved · ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`
          : 'Digest settings saved (no recipients — digest goes to your own inbox)'
      );
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const loadPreview = async () => {
    setPreviewing(true);
    try {
      const res = await brainDigestAPI.preview(config.frequency === 'daily' ? 1 : 7);
      setPreview(res.data);
    } catch { toast.error('Failed to load preview'); }
    finally { setPreviewing(false); }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      await brainDigestAPI.send(config.frequency === 'daily' ? 1 : 7);
      toast.success('Digest sent successfully');
      fetchData();
    } catch { toast.error('Failed to send digest'); }
    finally { setSending(false); }
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    if ((config.recipients || []).includes(email)) {
      toast.error('Email already added');
      return;
    }
    setConfig(prev => ({ ...prev, recipients: [...(prev.recipients || []), email] }));
    setNewEmail('');
  };

  const removeRecipient = (email) => {
    setConfig(prev => ({ ...prev, recipients: prev.recipients.filter(e => e !== email) }));
  };

  if (loading) return (
    <div className="space-y-4 p-6" data-testid="brain-digest-skeleton">
      <div className="flex justify-between items-center">
        <div className="h-7 w-40 bg-muted rounded animate-pulse" />
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-muted/60 rounded animate-pulse" />
          <div className="h-9 w-24 bg-muted/60 rounded animate-pulse" />
        </div>
      </div>
      <div className="rounded-lg border p-6 space-y-4">
        <div className="h-5 w-48 bg-muted rounded animate-pulse" />
        <div className="h-4 w-full bg-muted/40 rounded animate-pulse" />
        <div className="h-4 w-3/4 bg-muted/40 rounded animate-pulse" />
        <div className="h-4 w-5/6 bg-muted/40 rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-4 rounded-lg border space-y-2">
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
            <div className="h-3 w-full bg-muted/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="brain-digest-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="digest-title">Brain Digest</h1>
        <p className="text-muted-foreground text-sm">AI-powered executive summary of your pipeline activity</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Configuration */}
        <Card data-testid="digest-config-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4" /> Schedule</CardTitle>
            <CardDescription>Configure when and who receives the digest</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Enable Digest</span>
              <Switch checked={config.enabled} onCheckedChange={(v) => setConfig(prev => ({ ...prev, enabled: v }))} data-testid="digest-enable-toggle" />
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Frequency</label>
              <Select value={config.frequency} onValueChange={(v) => setConfig(prev => ({ ...prev, frequency: v }))}>
                <SelectTrigger data-testid="digest-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.frequency === 'weekly' && (
              <div>
                <label className="text-sm font-medium mb-1 block">Day of Week</label>
                <Select value={String(config.day_of_week)} onValueChange={(v) => setConfig(prev => ({ ...prev, day_of_week: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                      <SelectItem key={i} value={String(i)}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-1 block">Send Time (UTC)</label>
              <Select value={String(config.hour_utc)} onValueChange={(v) => setConfig(prev => ({ ...prev, hour_utc: parseInt(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>{`${i.toString().padStart(2, '0')}:00 UTC`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Recipients</label>
              <div className="flex gap-2 mb-2">
                <Input placeholder="email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addRecipient()} data-testid="digest-recipient-input" />
                <Button size="sm" variant="outline" onClick={addRecipient}><Plus className="h-3 w-3" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {config.recipients.map((email) => (
                  <Badge key={email} variant="secondary" className="text-xs gap-1">
                    {email}
                    <button onClick={() => removeRecipient(email)} className="hover:text-destructive"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
                {config.recipients.length === 0 && <span className="text-xs text-muted-foreground">No recipients added</span>}
              </div>
            </div>

            <Button onClick={saveConfig} disabled={saving} className="w-full" data-testid="digest-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save Settings
            </Button>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="space-y-4">
          <Card data-testid="digest-actions-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" onClick={loadPreview} disabled={previewing} data-testid="digest-preview-btn">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview Digest
              </Button>
              <Button className="w-full justify-start" onClick={sendDigest} disabled={sending} data-testid="digest-send-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send Now
              </Button>
            </CardContent>
          </Card>

          {/* History */}
          <Card data-testid="digest-history-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Send History</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No digests sent yet</p>
              ) : (
                <div className="space-y-2">
                  {history.slice(0, 5).map((log, i) => {
                    const ts = log.sent_at || log.created_at;
                    const d = ts ? new Date(ts) : null;
                    const dateStr = d && !isNaN(d.getTime()) ? d.toLocaleDateString() : '—';
                    const ok = log.success != null ? log.success : (log.sent || 0) > 0;
                    const recips = (log.recipients && log.recipients.length)
                      ? log.recipients.join(', ')
                      : (log.subject || 'Digest sent');
                    return (
                      <div key={i} className="flex items-center justify-between text-xs border-b pb-2 last:border-0">
                        <div className="flex items-center gap-2 min-w-0">
                          {ok ? <CheckCircle className="h-3 w-3 text-green-500 shrink-0" /> : <AlertCircle className="h-3 w-3 text-red-500 shrink-0" />}
                          <span className="truncate">{recips}</span>
                        </div>
                        <span className="text-muted-foreground shrink-0 ml-2">{dateStr}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <Card data-testid="digest-preview-content">
          <CardHeader>
            <CardTitle className="text-base">Digest Preview</CardTitle>
            <CardDescription>{preview.period || 'Last 7 days'}</CardDescription>
          </CardHeader>
          <CardContent>
            {preview.executive_summary ? (
              <div className="prose dark:prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview.executive_summary.replace(/\n/g, '<br/>')) }} />
            ) : preview.html_body ? (
              <div className="prose dark:prose-invert max-w-none text-sm" dangerouslySetInnerHTML={{ __html: preview.html_body }} />
            ) : (
              <div className="space-y-3 text-sm">
                <p><strong>Decisions:</strong> {preview.total_decisions || 0}</p>
                <p><strong>Leads Scored:</strong> {preview.leads_scored || 0}</p>
                <p><strong>Emails Drafted:</strong> {preview.emails_drafted || 0}</p>
                <p><strong>Tasks Created:</strong> {preview.tasks_created || 0}</p>
                {preview.top_leads?.length > 0 && (
                  <div>
                    <strong>Top Leads:</strong>
                    <ul className="mt-1 space-y-1">
                      {preview.top_leads.map((l, i) => (
                        <li key={i}>{l.name} ({l.company}) — Score: {l.ai_score}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
